import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth, useLocale } from '../../context';
import { api } from '../../api';
import {
  IconHistoryAmountArrow,
  IconHistoryExpander,
  IconHistoryFilter,
  IconHistoryMainLoss,
  IconHistoryMainWin,
} from '../VurgunIcons';
import RequestsEmptyState from '../account/RequestsEmptyState';
import { formatMoney } from '../../utils/currency';

const RESULT_FILTERS = ['all', 'not_settled', 'win', 'loss', 'cashed_out', 'void', 'return'];

function IconCopySmall() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.25 2.25C7.42157 2.25 6.75 2.92157 6.75 3.75V5.25H5.25C4.42157 5.25 3.75 5.92157 3.75 6.75V18.75C3.75 19.5784 4.42157 20.25 5.25 20.25H15.75C16.5784 20.25 17.25 19.5784 17.25 18.75V17.25H18.75C19.5784 17.25 20.25 16.5784 20.25 15.75V3.75C20.25 2.92157 19.5784 2.25 18.75 2.25H8.25ZM17.25 15.75H18.75V3.75H8.25V15.75H17.25ZM5.25 6.75H6.75V18.75H15.75V20.25H5.25V6.75Z"
        fill="currentColor"
      />
    </svg>
  );
}

function formatDayLabel(value, locale) {
  const date = new Date(value);
  const day = date.getDate();
  const month = date.toLocaleString(locale, { month: 'short' }).replace('.', '').toUpperCase();
  return `${day} ${month}`;
}

function formatTime(value) {
  const date = new Date(value);
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function formatAmountDisplay(value) {
  return formatMoney(Math.abs(Number(value || 0))).replace('.', ',');
}

function formatOdds(value) {
  if (value == null || value === '') return '—';
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  return num.toFixed(2);
}

function formatSignedAmount(amount, bonusAmount = 0) {
  const bonus = Number(bonusAmount || 0);
  if (bonus !== 0) {
    return `${formatAmountDisplay(amount)} / ${formatAmountDisplay(bonus)}`;
  }
  const value = Number(amount || 0);
  if (value < 0) return `-${formatAmountDisplay(Math.abs(value))}`;
  return formatAmountDisplay(value);
}

function mapResultFilter(item) {
  const map = {
    pending: 'not_settled',
    won: 'win',
    lost: 'loss',
    cancelled: 'void',
    completed: 'return',
  };
  return map[item.result] || item.result;
}

function isSportsBet(item) {
  return item.betType === 'sportsbook' || item.category === 'sport';
}

function groupByDay(items, locale) {
  const groups = [];
  let currentDay = null;
  items.forEach((item) => {
    const day = formatDayLabel(item.createdAt, locale);
    if (day !== currentDay) {
      groups.push({ day, items: [] });
      currentDay = day;
    }
    groups[groups.length - 1].items.push(item);
  });
  return groups;
}

function operationLabelKey(item) {
  if (item.kind === 'bet_placed' && item.source === 'slot') {
    return 'account.history.operation_slot_bet_placed';
  }
  return `account.history.operation_${item.kind}`;
}

function resultLabelKey(result) {
  return `account.history.result_${result || 'pending'}`;
}

function CouponSum({ item }) {
  const isWin = item.result === 'won';
  const isLoss = item.result === 'lost';
  const stake = Math.abs(Number(item.stake ?? item.amount ?? 0));
  const winAmount = item.winAmount != null ? Math.abs(Number(item.winAmount)) : null;
  const sumClass = `mobile-account-history__sum${isLoss ? ' is-loss' : ''}${isWin ? ' is-win' : ''}`;

  if (isWin && winAmount != null && winAmount > 0) {
    const hasStake = Number(stake || 0) > 0.009;
    return (
      <div className={sumClass}>
        <span className="mobile-account-history__sum-icon is-win" aria-hidden="true">
          <IconHistoryMainWin />
        </span>
        {hasStake ? (
          <>
            <span className="mobile-account-history__sum-muted">{formatAmountDisplay(stake)}</span>
            <span className="mobile-account-history__sum-arrow" aria-hidden="true">
              <IconHistoryAmountArrow />
            </span>
          </>
        ) : null}
        <span className="mobile-account-history__sum-value">{formatAmountDisplay(winAmount)}</span>
      </div>
    );
  }

  if (isLoss) {
    return (
      <div className={sumClass}>
        <span className="mobile-account-history__sum-icon is-loss" aria-hidden="true">
          <IconHistoryMainLoss />
        </span>
        <span className="mobile-account-history__sum-value">{formatAmountDisplay(stake)}</span>
      </div>
    );
  }

  return (
    <div className={sumClass}>
      <span className="mobile-account-history__sum-value">{formatAmountDisplay(stake)}</span>
    </div>
  );
}

function CouponDetails({ item, tr }) {
  const selections = Array.isArray(item.selections) ? item.selections : [];
  return (
    <div className="mobile-account-history__details">
      <div className="mobile-account-history__details-row">
        <span>{tr('account.history.colCoupon')}</span>
        <strong>{item.couponNo || item.betNo || '—'}</strong>
      </div>
      <div className="mobile-account-history__details-row">
        <span>{tr('account.history.colStake')}</span>
        <strong>{item.stake != null ? `₺${formatAmountDisplay(item.stake)}` : '—'}</strong>
      </div>
      <div className="mobile-account-history__details-row">
        <span>{tr('account.history.colOdds')}</span>
        <strong>{formatOdds(item.odds)}</strong>
      </div>
      <div className="mobile-account-history__details-row">
        <span>{tr('account.history.colPotential')}</span>
        <strong>{item.potentialWin != null ? `₺${formatAmountDisplay(item.potentialWin)}` : '—'}</strong>
      </div>
      <div className="mobile-account-history__details-row">
        <span>{tr('account.history.colStatus')}</span>
        <strong>{tr(resultLabelKey(item.result))}</strong>
      </div>
      {selections.length > 0 ? (
        <ul className="mobile-account-history__details-sels">
          {selections.map((sel, index) => (
            <li key={`${item.id}-m-sel-${index}`}>
              <div>{sel.match || [sel.homeTeam, sel.awayTeam].filter(Boolean).join(' — ') || '—'}</div>
              <div className="mobile-account-history__details-meta">
                {[sel.typeLabel || (sel.type === 'live' ? 'Canlı' : sel.type === 'line' ? 'Maç öncesi' : null), sel.market, sel.pick].filter(Boolean).join(' · ')}
                {sel.odds != null ? ` · ${formatOdds(sel.odds)}` : ''}
              </div>
            </li>
          ))}
        </ul>
      ) : item.description ? (
        <div className="mobile-account-history__details-desc">{item.description}</div>
      ) : null}
    </div>
  );
}

function BetCoupon({ item, tr, onCopy, expanded, onToggle }) {
  const resultKey = resultLabelKey(item.result);
  const isWin = item.result === 'won';
  const isLoss = item.result === 'lost';
  const sports = isSportsBet(item);
  const [provider, game] = String(item.description || '').split(' | ');
  const displayNo = item.couponNo || item.betNo;

  return (
    <div className={`mobile-account-history__coupon${expanded ? ' is-expanded' : ''}`}>
      <button
        type="button"
        className="mobile-account-history__coupon-main"
        onClick={() => sports && onToggle()}
        disabled={!sports}
      >
        <div className="mobile-account-history__coupon-row1">
          <span className={`mobile-account-history__result${isLoss ? ' is-loss' : ''}${isWin ? ' is-win' : ''}`}>
            {tr(resultKey)}
          </span>
          <CouponSum item={item} />
        </div>
        <div className="mobile-account-history__coupon-row2">
          <div className="mobile-account-history__coupon-meta">
            <span className="mobile-account-history__num"># {displayNo}</span>
            <button
              type="button"
              className="mobile-account-history__copy"
              aria-label={tr('mobile.account.copy')}
              onClick={(event) => {
                event.stopPropagation();
                onCopy(displayNo);
              }}
            >
              <IconCopySmall />
            </button>
            <span className="mobile-account-history__time">{formatTime(item.createdAt)}</span>
          </div>
          {sports && (
            <span className={`mobile-account-history__chevron${expanded ? ' is-open' : ''}`} aria-hidden="true">
              <IconHistoryExpander />
            </span>
          )}
        </div>
        <div className="mobile-account-history__coupon-body">
          <div className="mobile-account-history__desc" title={item.description}>
            {provider && game ? (
              <>
                <span className="mobile-account-history__desc-part">{provider}</span>
                <span className="mobile-account-history__desc-sep"> | </span>
                <span className="mobile-account-history__desc-part">{game}</span>
              </>
            ) : (
              item.description
            )}
          </div>
          {(sports || item.category === 'slot') && (
            <span className="mobile-account-history__sticker">
              {tr(sports ? 'account.history.betTypeSportsbook' : 'account.history.betTypeSlot')}
            </span>
          )}
        </div>
      </button>
      {expanded && sports && <CouponDetails item={item} tr={tr} />}
    </div>
  );
}

function OperationRow({ item, tr }) {
  const amount = Number(item.amount || 0);
  const bonusAmount = Number(item.bonusAmount || 0);
  const isNegative = bonusAmount === 0 && amount < 0;
  const label = tr(operationLabelKey(item));

  return (
    <div className="mobile-account-history__op">
      <div className="mobile-account-history__op-line">
        <span className="mobile-account-history__op-text">
          <span className="mobile-account-history__op-label">{label}</span>
          <span className="mobile-account-history__op-meta">
            {' '}
            #
            {item.operationNo}
            {' '}
            {formatTime(item.createdAt)}
          </span>
        </span>
        <span className={`mobile-account-history__op-amount${isNegative ? ' is-negative' : ''}`}>
          {formatSignedAmount(amount, bonusAmount)}
        </span>
      </div>
    </div>
  );
}

function HistoryFilter({
  filterRef,
  filterOpen,
  setFilterOpen,
  resultOptions,
  resultFilters,
  toggleResultFilter,
  tr,
}) {
  return (
    <div className="mobile-account-history__filter-sticky">
      <div className="mobile-account-history__filter-wrap" ref={filterRef}>
        <button
          type="button"
          className={`mobile-account-history__filter${filterOpen ? ' is-open' : ''}`}
          aria-expanded={filterOpen}
          aria-label={tr('account.history.filters')}
          onClick={() => setFilterOpen((open) => !open)}
        >
          <IconHistoryFilter />
        </button>
        {filterOpen && (
          <div className="mobile-account-history__filter-popup">
            <div className="mobile-account-history__filter-title">{tr('account.history.filterGroupResult')}</div>
            <div className="mobile-account-history__filter-list">
              {resultOptions.map((option) => {
                const active = option.id === 'all'
                  ? resultFilters === null
                  : resultFilters?.has(option.id);
                return (
                  <button
                    key={option.id}
                    type="button"
                    className={`mobile-account-history__filter-item${active ? ' is-active' : ''}`}
                    onClick={() => toggleResultFilter(option.id)}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function MobileAccountHistory() {
  const _locale = useLocale();
  const tr = _locale.t;
  const locale = _locale.locale;
  const { token } = useAuth();
  const [tab, setTab] = useState('bets');
  const [bets, setBets] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resultFilters, setResultFilters] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [expandedBetId, setExpandedBetId] = useState(null);
  const filterRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setBets([]);
      setTransactions([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    Promise.all([api.historyBets(token), api.historyTransactions(token)])
      .then(([betItems, txItems]) => {
        if (cancelled) return;
        setBets(Array.isArray(betItems) ? betItems : (betItems?.items || []));
        setTransactions(Array.isArray(txItems) ? txItems : (txItems?.items || []));
      })
      .catch(() => {
        if (cancelled) return;
        setBets([]);
        setTransactions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (!filterOpen) return undefined;
    const onDocClick = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [filterOpen]);

  const filteredBets = useMemo(() => (Array.isArray(bets) ? bets : []).filter((item) => {
    if (resultFilters === null) return true;
    return resultFilters.has(mapResultFilter(item));
  }), [bets, resultFilters]);

  const betGroups = useMemo(
    () => groupByDay(filteredBets, locale),
    [filteredBets, locale],
  );

  const txGroups = useMemo(
    () => groupByDay(Array.isArray(transactions) ? transactions : [], locale),
    [transactions, locale],
  );

  const resultOptions = useMemo(() => RESULT_FILTERS.map((id) => ({
    id,
    label: tr(`account.history.filterResult_${id}`),
  })), [tr]);

  const toggleResultFilter = (id) => {
    if (id === 'all') {
      setResultFilters(null);
      return;
    }
    if (resultFilters === null) {
      setResultFilters(new Set([id]));
      return;
    }
    const next = new Set(resultFilters);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setResultFilters(next.size ? next : null);
  };

  const copyBetNo = async (betNo) => {
    try {
      await navigator.clipboard.writeText(String(betNo));
    } catch {
      /* ignore */
    }
  };

  const tabs = [
    { id: 'bets', label: tr('account.history.tabBets') },
    { id: 'transactions', label: tr('account.history.tabTransactions') },
  ];

  const filterProps = {
    filterRef,
    filterOpen,
    setFilterOpen,
    resultOptions,
    resultFilters,
    toggleResultFilter,
    tr,
  };

  return (
    <div className="mobile-account-history">
      <div className="mobile-account-history__float-bar">
        <div className="mobile-account-history__tabs" role="tablist">
          {tabs.map((item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={tab === item.id}
              className={`mobile-account-history__tab${tab === item.id ? ' is-active' : ''}`}
              onClick={() => {
                setTab(item.id);
                setFilterOpen(false);
                setExpandedBetId(null);
              }}
            >
              <span className="mobile-account-history__tab-label">{item.label}</span>
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="mobile-account-history__loading">{tr('account.loading')}</div>
      ) : tab === 'bets' ? (
        <div className="mobile-account-history__pane mobile-account-history__pane--bets">
          <HistoryFilter {...filterProps} />
          {filteredBets.length === 0 ? (
            <RequestsEmptyState messageKey="account.empty" />
          ) : (
            <div className="mobile-account-history__list">
              {betGroups.map((group) => (
                <section key={group.day} className="mobile-account-history__day-group">
                  <div className="mobile-account-history__day-sticky">
                    <div className="mobile-account-history__day-row">{group.day}</div>
                  </div>
                  {group.items.map((item) => (
                    <BetCoupon
                      key={item.id}
                      item={item}
                      tr={tr}
                      onCopy={copyBetNo}
                      expanded={expandedBetId === item.id}
                      onToggle={() => setExpandedBetId((id) => (id === item.id ? null : item.id))}
                    />
                  ))}
                </section>
              ))}
            </div>
          )}
        </div>
      ) : transactions.length === 0 ? (
        <RequestsEmptyState messageKey="account.empty" />
      ) : (
        <div className="mobile-account-history__pane mobile-account-history__pane--transactions">
          <div className="mobile-account-history__list mobile-account-history__list--operations">
            {txGroups.map((group) => (
              <section key={group.day} className="mobile-account-history__day-group">
                <div className="mobile-account-history__day-sticky">
                  <div className="mobile-account-history__day-row">{group.day}</div>
                </div>
                {group.items.map((item) => (
                  <OperationRow key={item.id} item={item} tr={tr} />
                ))}
              </section>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
